/**
 * 개발용 「테스트 작업 시작」 설정.
 *
 * eric 원문(2026-09-22): 「작업 탭 중에 항상 실행할 수 있는 작업을 프론트쪽 코드에
 * 하드코딩해줘. 계속 선택해서 하려니 귀찮어. 나중에 지울 거야. 아니면 해당 더미 작업을
 * 클릭하면 실제 백엔드에 상태를 변동시켜서 동작하게끔 해도 돼.」 → **실제 백엔드를 움직인다.**
 * 화면에만 가짜 작업을 박으면 서버가 SSOT 라는 규약(DP-04)이 깨지고 장비가 돌지 않는다.
 *
 * ## 🔴 「있다/없다」가 설정으로 갈린다 — 규약의 «예외»다
 *
 * 권한 없는 버튼은 숨기지 않고 비활성 + 사유가 원칙인데(§5-6) **이 버튼만 아예 없앤다.**
 * 권한이 아니라 **개발 편의 기능이라서**다 — 고객 화면에 「테스트 작업」이 비활성으로라도
 * 서 있으면 그건 제품의 일부로 읽힌다. 그래서 축이 다르고, 축이 다르니 규칙도 다르다.
 * ⇒ `VITE_DEV_QUICK_BATCH=true` 가 아니면 **DOM 에 없다.** 시험이 그 자리를 지킨다.
 *
 * ## 🔴 레시피 id 를 코드에 박지 않는다
 *
 * 「끝까지 도는 레시피」는 이 로컬 DB 의 사정이지 제품의 사실이 아니다. 지금 값이
 * `fe-20260922-full-r1` 이라고 코드에 적으면 다른 DB 에서 **없는 것을 가리키는 버튼**이 된다.
 * ⇒ `VITE_DEV_QUICK_BATCH_RECIPE` 로 받고, **비면 버튼은 서되 비활성 + 「레시피 미지정」**이다.
 *   (버튼이 「없음」이 아니라 「비활성」인 것은 여기서는 **켜 놓고 값만 빠뜨린 상태**라
 *    사람이 무엇을 채워야 하는지 알아야 하기 때문이다.)
 *
 * 값의 출처는 둘이고 **런타임이 이긴다** — `visionConfig.ts` 와 같은 규칙이다.
 */
import { runtimeSection } from './runtimeConfig.ts'

export interface DevQuickBatchConfig {
  /** 버튼이 **존재**하는가. 거짓이면 그리지 않는다 */
  enabled: boolean
  /** 만들 작업이 쓸 레시피. 없으면 버튼은 비활성 + 사유 */
  recipeId: string | undefined
}

export interface DevQuickBatchInput {
  runtime?: Record<string, unknown> | undefined
  env?: { enabled?: string | undefined; recipeId?: string | undefined } | undefined
}

export function resolveDevQuickBatch(input: DevQuickBatchInput): DevQuickBatchConfig {
  const runtime = input.runtime ?? {}
  const env = input.env ?? {}
  return {
    enabled: flag(runtime['enabled']) ?? flag(env.enabled) ?? false,
    recipeId: text(runtime['recipeId']) ?? text(env.recipeId),
  }
}

let cached: DevQuickBatchConfig | undefined

export function devQuickBatchConfig(): DevQuickBatchConfig {
  cached ??= resolveDevQuickBatch({
    runtime: runtimeSection('devQuickBatch'),
    env: {
      enabled: import.meta.env.VITE_DEV_QUICK_BATCH,
      recipeId: import.meta.env.VITE_DEV_QUICK_BATCH_RECIPE,
    },
  })
  return cached
}

/** 테스트 전용 */
export function resetDevQuickBatchCache(): void {
  cached = undefined
}

/**
 * 🔴 **`true` 한 낱말만 켠다.** `1`·`yes`·빈 문자열을 받아 주면 **꺼 놓은 줄 알았는데 켜져 있는**
 *    경우가 생긴다 — 이 스위치는 고객 화면에 개발용 버튼이 뜨느냐를 가른다.
 */
function flag(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  const raw = text(value)
  return raw === undefined ? undefined : raw === 'true'
}

function text(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}
